import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { OptionalLink } from '../01-core/OptionalLink';

export interface TabProps {
  label: string;
  controls?: string;
}

export interface TabsProps {
  items: TabProps[];
  currentTab?: number;
  defaultTab?: number;
  className?: string;
  onTabChange?: (tab: number) => void;
  enableScroll?: boolean;
}

const SCROLL_THRESHOLD = 5;

export const Tabs = ({
  items,
  currentTab,
  defaultTab = 0,
  className,
  onTabChange,
  enableScroll = false,
  ...props
}: TabsProps) => {
  const isControlled = currentTab !== undefined;

  const getInitialTab = () => {
    if (isControlled) return 0;
    return Math.min(Math.max(defaultTab, 0), Math.max(items.length - 1, 0));
  };

  const [internalTab, setInternalTab] = useState(getInitialTab);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isControlled && items.length > 0) {
      setInternalTab((prevTab) =>
        Math.min(prevTab, Math.max(items.length - 1, 0)),
      );
    }
  }, [items.length, isControlled]);

  const activeTab = isControlled ? currentTab! : internalTab;

  const switchTab = useCallback(
    (index: number) => {
      if (!isControlled) {
        setInternalTab(index);
      }

      if (onTabChange) {
        onTabChange(index);
      }
    },
    [isControlled, onTabChange],
  );

  const checkScrollPosition = useCallback(() => {
    if (!enableScroll || !navRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = navRef.current;

    setCanScrollLeft(scrollLeft > SCROLL_THRESHOLD);
    setCanScrollRight(
      scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD,
    );
  }, [enableScroll]);

  const scrollToTab = useCallback(
    (tabIndex: number) => {
      if (!enableScroll || !navRef.current || tabIndex < 0) return;

      const tabElement = navRef.current.children[tabIndex] as HTMLElement;
      if (!tabElement) return;

      const computed = window.getComputedStyle(navRef.current);
      const left =
        tabElement.offsetLeft -
        parseInt(computed.getPropertyValue('padding-left') || '0');
      const right =
        tabElement.offsetLeft +
        tabElement.offsetWidth +
        parseInt(computed.getPropertyValue('padding-right') || '0');
      const curRight = navRef.current.scrollLeft + navRef.current.offsetWidth;
      const curLeft = navRef.current.scrollLeft;

      if (left < curLeft) {
        navRef.current.scrollTo({ behavior: 'smooth', left: left });
      } else if (right > curRight) {
        navRef.current.scrollTo({
          behavior: 'smooth',
          left: right - navRef.current.offsetWidth,
        });
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          checkScrollPosition();
        });
      });
    },
    [enableScroll, checkScrollPosition],
  );

  useEffect(() => {
    if (!enableScroll || !navRef.current) return;

    const navElement = navRef.current;

    const handleScroll = (event: Event) => {
      if (event.target === navElement) {
        checkScrollPosition();
      }
    };

    navElement.addEventListener('scroll', handleScroll, { passive: true });

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => checkScrollPosition());
      resizeObserver.observe(navElement);
    } else {
      const handleResize = () => {
        requestAnimationFrame(() => checkScrollPosition());
      };
      window.addEventListener('resize', handleResize);
    }

    requestAnimationFrame(() => {
      checkScrollPosition();
      scrollToTab(activeTab);
    });

    return () => {
      navElement.removeEventListener('scroll', handleScroll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', checkScrollPosition);
      }
    };
  }, [enableScroll, activeTab, items, checkScrollPosition, scrollToTab]);

  const classNames = useMemo(() => {
    const classes = ['tabs'];

    if (className) classes.push(className);
    if (enableScroll) {
      classes.push('tabs--scrollable');
      if (canScrollLeft) classes.push('gradient_next');
      if (canScrollRight) classes.push('gradient_prev');
    }

    return classes.join(' ');
  }, [className, enableScroll, canScrollLeft, canScrollRight]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (activeTab > 0) {
            switchTab(activeTab - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (activeTab < items.length - 1) {
            switchTab(activeTab + 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          switchTab(0);
          break;
        case 'End':
          event.preventDefault();
          switchTab(items.length - 1);
          break;
      }
    },
    [items.length, switchTab, activeTab],
  );

  return (
    <div className={enableScroll ? 'tabs__container' : undefined}>
      <div
        ref={navRef}
        className={classNames}
        role="tablist"
        {...props}
        onKeyDown={(event) => handleKeyDown(event)}
      >
        {items.map((item, index) => {
          const key = item.controls || `${item.label}-${index}`;

          return (
            <OptionalLink
              key={key}
              className={`tabs__item ${index === activeTab ? 'tabs__item--current' : ''}`}
              role="tab"
              button={true}
              onClick={() => switchTab(index)}
              aria-selected={index === activeTab ? 'true' : 'false'}
              aria-controls={item.controls}
              {...item}
            >
              {item.label}
            </OptionalLink>
          );
        })}
      </div>
    </div>
  );
};
